import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface TranslationSelectFieldsProps {
  sourceText: string;
  options: string[];
  correctAnswerIndex: number;
  onChange: (updates: Partial<ActivityFormData>) => void;
  errors?: {
    sourceText?: string;
    options?: string;
  };
}

export function TranslationSelectFields({
  sourceText,
  options,
  correctAnswerIndex,
  onChange,
  errors,
}: TranslationSelectFieldsProps) {
  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onChange({ options: newOptions });
  };

  const handleAddOption = () => {
    onChange({ options: [...options, ''] });
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) return;
    const newOptions = options.filter((_, i) => i !== index);
    const newCorrect = index < correctAnswerIndex
      ? correctAnswerIndex - 1
      : correctAnswerIndex >= newOptions.length
        ? newOptions.length - 1
        : correctAnswerIndex;
    onChange({ options: newOptions, correctAnswerIndex: newCorrect });
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Selección de la traducción correcta</h3>
      <p className={styles.typeFields__description}>
        Se muestra una frase y el alumno selecciona la traducción correcta de entre 3–4 opciones.
      </p>

      <div>
        <label htmlFor="sourceTextTS" className={styles.typeFields__title}>
          Frase fuente (del script)
        </label>
        <Input
          id="sourceTextTS"
          value={sourceText}
          onChange={(e) => onChange({ sourceText: e.target.value })}
          placeholder="Ej: She goes to the market every Sunday."
        />
        <p className={styles.typeFields__hint}>Puede ser en inglés o en español según el ejercicio.</p>
        {errors?.sourceText && (
          <span className={styles.typeFields__error}>{errors.sourceText}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>
          Opciones de traducción ({options.length}) — marca la correcta con el radio
        </label>
        <div className={styles.typeFields__list}>
          {options.map((option, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <div className={styles.typeFields__optionRadio}>
                <input
                  type="radio"
                  className={styles.typeFields__radio}
                  name="correctAnswerIndex-ts"
                  checked={correctAnswerIndex === index}
                  onChange={() => onChange({ correctAnswerIndex: index })}
                  aria-label={`Marcar opción ${index + 1} como correcta`}
                />
              </div>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Opción ${index + 1}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveOption(index)}
                disabled={options.length <= 2}
                aria-label={`Eliminar opción ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddOption}
          className={styles.typeFields__addButton}
        >
          Añadir opción
        </Button>
        {errors?.options && (
          <span className={styles.typeFields__error}>{errors.options}</span>
        )}
      </div>
    </div>
  );
}
